import React from "react";
import { useOutletContext } from "react-router-dom";
import { Mail, Phone, MapPin, MessageCircle } from "lucide-react";
import ContactForm from "../components/home/ContactForm";
import BitrixContactForm from "../components/home/BitrixContactForm";
import { useI18n } from "../lib/i18n";

const ContactPage = () => {
  const { t, pick } = useI18n();
  const ctx = useOutletContext() || {};
  const { settings, ready } = ctx;

  return (
    <div data-testid="contact-page">
      {/* Header block — inline compact eyebrow + title (no big hero banner, no empty gap) */}
      <section className="pt-32 md:pt-36 pb-6 md:pb-10 bg-white">
        <div className="container-x">
          <div className="flex items-center gap-4 mb-5">
            <span className="h-px w-12 bg-brand-primary" />
            <span className="text-[0.7rem] tracking-[0.36em] uppercase text-brand-primary font-medium">
              {t("nav.contact")}
            </span>
          </div>
          <h1
            className="font-serif text-brand-navy leading-[1.02] tracking-tight max-w-4xl"
            style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}
          >
            {pick({
              tr: "Konuşalım. Birlikte inşa edelim.",
              en: "Let’s talk. Let’s build together.",
              ar: "لنتحدث. لنبنِ معاً.",
            })}
          </h1>
          <p className="mt-5 text-brand-medium max-w-2xl leading-relaxed">
            {pick({
              tr: "Bir konu seçin — ekibimiz size 24 saat içinde dönüş yapsın. 3 kıtada 4 ofisimizle projeleriniz için yanınızdayız.",
              en: "Pick a topic — our team gets back within 24 hours. With 4 offices across 3 continents we stand by your projects.",
              ar: "اختر موضوعاً — يعود إليك فريقنا خلال 24 ساعة. 4 مكاتب في 3 قارات لدعم مشاريعك.",
            })}
          </p>
        </div>
      </section>

      {/* Main contact block */}
      <section className="bg-white">
        <div className="container-x pb-16 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left — Info */}
          <div className="lg:col-span-5">
            <div className="space-y-0">
              {settings?.contact_phone ? (
                <a
                  href={`tel:${settings.contact_phone.replace(/\s/g, "")}`}
                  className="group flex items-center gap-4 py-4 border-t border-black/10 hover:border-brand-primary transition-colors"
                  data-testid="contact-phone"
                >
                  <span className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center group-hover:bg-brand-navy group-hover:text-white transition-colors duration-300">
                    <Phone size={16} strokeWidth={1.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-medium">
                      {pick({ tr: "Telefon", en: "Phone", ar: "الهاتف" })}
                    </div>
                    <div className="mt-0.5 font-serif text-lg md:text-xl text-brand-navy">
                      {settings.contact_phone}
                    </div>
                  </div>
                </a>
              ) : null}

              {settings?.whatsapp_number ? (
                <a
                  href={`https://wa.me/${settings.whatsapp_number}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 py-4 border-t border-black/10 hover:border-[#25D366] transition-colors"
                  data-testid="contact-whatsapp"
                >
                  <span className="w-10 h-10 rounded-full bg-[#25D366]/15 text-[#128C7E] flex items-center justify-center group-hover:bg-[#25D366] group-hover:text-white transition-colors duration-300">
                    <MessageCircle size={16} strokeWidth={1.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-medium">WhatsApp</div>
                    <div className="mt-0.5 font-serif text-lg md:text-xl text-brand-navy">
                      +90 533 728 01 02
                    </div>
                  </div>
                </a>
              ) : null}

              {settings?.contact_email ? (
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="group flex items-center gap-4 py-4 border-t border-b border-black/10 hover:border-brand-primary transition-colors"
                  data-testid="contact-email"
                >
                  <span className="w-10 h-10 rounded-full bg-brand-primary/10 text-brand-primary flex items-center justify-center group-hover:bg-brand-navy group-hover:text-white transition-colors duration-300">
                    <Mail size={16} strokeWidth={1.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-medium">
                      {pick({ tr: "E-posta", en: "Email", ar: "البريد الإلكتروني" })}
                    </div>
                    <div className="mt-0.5 font-serif text-lg md:text-xl text-brand-navy break-all">
                      {settings.contact_email}
                    </div>
                  </div>
                </a>
              ) : null}
            </div>

            {(settings?.offices || []).length ? (
              <div className="mt-12">
                <div className="text-[0.6rem] tracking-[0.32em] uppercase text-brand-medium mb-5">
                  {pick({ tr: "Global Ofisler", en: "Global Offices", ar: "مكاتبنا حول العالم" })}
                </div>
                <div className="grid grid-cols-1 gap-5">
                  {(settings.offices || []).map((o, i) => (
                    <div
                      key={i}
                      className="flex items-start gap-3 py-3 border-t border-black/5"
                      data-testid={`contact-office-${i}`}
                    >
                      <MapPin size={16} strokeWidth={1.5} className="text-brand-primary mt-1 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="font-serif text-brand-navy text-base md:text-lg leading-tight">
                          {pick(o.name, "")}
                        </div>
                        <div className="text-brand-medium text-sm mt-1">{pick(o.address, "")}</div>
                        {o.phone ? (
                          <a
                            href={`tel:${o.phone.replace(/\s/g, "")}`}
                            className="text-brand-dark text-sm mt-1 inline-block hover:text-brand-primary transition-colors"
                          >
                            {o.phone}
                          </a>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
          </div>

          {/* Right — Bitrix multi-topic form (wait for settings to avoid a fallback-form flash) */}
          <div className="lg:col-span-7">
            {!ready ? (
              <div className="min-h-[520px] bg-brand-light animate-pulse" data-testid="contact-form-loading" />
            ) : (settings?.bitrix_forms || []).length ? (
              <BitrixContactForm forms={settings.bitrix_forms} />
            ) : (
              <ContactForm />
            )}
          </div>
        </div>
      </section>

      {/* HQ map */}
      <section className="bg-brand-light">
        <div className="container-x py-14 md:py-16">
          <div className="w-full h-[420px] bg-white border border-black/10 relative overflow-hidden" data-testid="contact-map">
            <iframe
              title="HQ"
              className="w-full h-full"
              src="https://www.openstreetmap.org/export/embed.html?bbox=28.68%2C41.15%2C28.80%2C41.24&layer=mapnik&marker=41.1965%2C28.7357"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
